import React, { useMemo, useState, useCallback } from 'react'
import { BaseDrawer } from '@/components/drawer/base-drawer'
import { layerManage, showLayer } from '@/utils/layer'
import { Expaned, Row, Column } from '@/components/layout/layout'
import { DatePicker, Radio, Table, Spin } from 'antd'
import dayjs from 'dayjs'
import { getBEMCls } from '@/utils/classname'
import classnames from 'classnames'
import FormTitle from '../../../components/form/form-title/form-title'

import './stock-join-form.scss'
import { ColumnsType } from 'antd/es/table'
import { StockInGoodsList } from '../stock-join-table/stock-join-table'
import LayoutInput from '../../../components/form/layout-input/layout-input'
import { useRequest, usePersistFn } from 'ahooks'
import { $post } from '@/api'
import useSetDataTogether from '@/utils/hooks/useSetDataTogether'
import { deepClone, setObj } from '@/utils'
import Button from '@/components/button/button'
import $msg from '@/utils/message'
import Big from 'big.js'
import moment from 'moment'
import StockJoinPreview from './stock-join-preview'

interface StockJoinFormProps {
    id: number
    refresh: () => void
}

const StockJoinForm: React.FC<StockJoinFormProps> = props => {
    const formatDate = useMemo(() => (date: dayjs.Dayjs) => date.format('MM-DD'), [])
    const cls = (...arg: string[]) => getBEMCls('stock-join-form', ...arg)

    const [day, setDay] = useState<number>(0)
    const [eta, setEta] = useState<string>(() => dayjs().format('YYYY-MM-DD'))
    const [loading, setLoading] = useState<boolean>(true)
    const [stockInList, setStockInList] = useState<StockInGoodsList[]>([])
    const [source, setSource] = useState<'purchase' | 'transfer'>('purchase')
    const setAllData = useSetDataTogether()

    const handleDay = useCallback((day: number) => {
        setDay(day)
        setEta(dayjs().add(day, 'day').format('YYYY-MM-DD'))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useRequest(
        () =>
            $post({
                url: '/warehouse/getstockinorderdetail',
                params: {
                    order_id: props.id,
                },
            }),
        {
            onSuccess: res => {
                setAllData(() => {
                    setSource(res.order_info.source)
                    setStockInList(
                        res.order_goods_infos.map((i: StockInGoodsList) => ({
                            ...i,
                            direct_storage: 1,
                        })),
                    )
                    setLoading(false)
                })
            },
        },
    )
    /**
     * 成本价计算
     */
    const computeCostPrice = usePersistFn((record: StockInGoodsList) => {
        const timesBoth = (a: number, b: number) => Big(a).times(b).round(2)
        const isDirect = record.direct_storage === 1
        // 商品成本价存在
        if (record.goods.cost_price) {
            if (record.purchase_price) {
                if (record.goods.stock < 0) {
                    const context = !isDirect
                        ? record.purchase_price
                        : record.stocked_in_amount
                        ? +Big(record.purchase_price)
                              .times(record.stocking_in_amount)
                              .round(2)
                              .div(record.stocked_in_amount)
                        : 0

                    return +Big(context).div(record.stock_in_unit_ratio).round(2)
                }
                const top = timesBoth(record.goods.cost_price, record.goods.stock).plus(
                    +timesBoth(
                        record.purchase_price,
                        isDirect ? record.stocking_in_amount : record.stocked_in_amount || 0,
                    ),
                )
                const bottom = timesBoth(record.stocked_in_amount || 0, record.stock_in_unit_ratio).plus(
                    record.goods.stock,
                )

                return +bottom ? +top.div(+bottom).round(2) : 0
            } else {
                return record.goods.cost_price
            }
        } else {
            if (record.purchase_price) {
                const top = timesBoth(
                    record.purchase_price,
                    isDirect ? record.stocking_in_amount : record.stocked_in_amount || 0,
                )

                const bottom = timesBoth(record.stocked_in_amount || 0, record.stock_in_unit_ratio)

                return +bottom ? +top.div(+bottom).round(2) : 0
            } else {
                return null
            }
        }
    })

    const columns: ColumnsType<StockInGoodsList> = [
        {
            title: '#',
            key: 'index',
            width: 50,
            render: (value, record, index) => index + 1,
        },
        {
            title: '商品名',
            key: 'goods',
            ellipsis: true,
            dataIndex: 'goods',
            render: val => val.name,
        },
        ...(source === 'purchase'
            ? [
                  {
                      title: '供货商',
                      key: 'supplier',
                      ellipsis: true,
                      dataIndex: 'supplier',
                      render: (val: StockInGoodsList['supplier'] | null) => (val && val.name) || '-',
                  },
              ]
            : []),
        {
            title: source === 'transfer' ? '待入库量' : '采购量',
            key: 'stocking_in_amount',
            dataIndex: 'stocking_in_amount',
            render: (v, r) => `${v}${r.stock_in_unit.name}`,
        },
        {
            title: '入库量',
            key: 'stocked_in_amount',
            width: 130,
            dataIndex: 'stocked_in_amount',
            render: (val, record, index) => (
                <Row data-align-items-center>
                    <LayoutInput
                        value={val}
                        placeholder="请录入"
                        onBlur={e => {
                            setStockInList(
                                deepClone(
                                    setObj(
                                        setObj(stockInList, `[${index}].stocked_in_amount`, e),
                                        `[${index}].cost_price`,
                                        computeCostPrice(record),
                                    ),
                                ),
                            )
                        }}
                    />
                    {record.stock_in_unit.name}
                </Row>
            ),
        },
        ...(source === 'purchase'
            ? [
                  {
                      title: '成本价',
                      width: 90,
                      key: 'cost_price',
                      render: (_val: unknown, record: StockInGoodsList) => {
                          if (record.stocked_in_amount) {
                              const r = computeCostPrice(record)

                              return r ? `¥${r}` : '-'
                          } else {
                              return '-'
                          }
                      },
                  },
                  {
                      title: '入库方式',
                      width: 240,
                      key: 'direct_storage',
                      dataIndex: 'direct_storage',
                      align: 'center' as 'center',
                      render: (val: number, record: StockInGoodsList, index: number) => {
                          return !record.stocked_in_amount || record.stocking_in_amount <= record.stocked_in_amount ? (
                              '-'
                          ) : (
                              <Row>
                                  <Radio.Group
                                      onChange={e => {
                                          setStockInList(
                                              deepClone(
                                                  setObj(stockInList, `[${index}].direct_storage`, e.target.value),
                                              ),
                                          )
                                      }}
                                      value={val}
                                  >
                                      <Radio value={1}>直接入库</Radio>
                                      <Radio value={0}>部分入库</Radio>
                                  </Radio.Group>
                              </Row>
                          )
                      },
                  },
              ]
            : []),
    ]

    const btnDisabled = useMemo(
        () =>
            !stockInList.length ||
            stockInList.some(
                s =>
                    s.stocked_in_amount === null ||
                    s.stocked_in_amount === undefined ||
                    `${s.stocked_in_amount}`.trim() === '',
            ),
        [stockInList],
    )

    const createStockIn = useRequest(
        params =>
            $post({
                url: '/warehouse/stockin',
                params,
            }),
        {
            manual: true,
            onSuccess: () => {
                $msg('success', '入库成功')
                layerManage.popAll()
                props.refresh()
            },
            onError: e => $msg('error', e.message),
        },
    )

    const handlePreview = () => {
        if (source === 'transfer') {
            handleSubmit(false, false)

            return
        }
        const stockOutList = stockInList
            .map(i =>
                i.direct_storage === 0
                    ? {
                          ...i,
                          stocked_in_amount: +Big(+i.stocking_in_amount).minus(+i.stocked_in_amount),
                      }
                    : void 0,
            )
            .filter(i => !!i)

        showLayer(
            <StockJoinPreview
                onSubmit={is_create_new => {
                    handleSubmit(false, is_create_new)
                }}
                stockInList={stockInList}
                stockOutList={stockOutList as StockInGoodsList[]}
                loading={createStockIn.loading}
            />,
        )
    }

    const handleSubmit = (sub: boolean, is_create_new: boolean) => {
        const formatList = stockInList.map(i =>
            sub
                ? {
                      cost_price: computeCostPrice(setObj(i, 'stocked_in_amount', i.stocking_in_amount)),
                      direct_storage: true,
                      goods_name: i.goods.name,
                      order_goods_id: i.id,
                      stock_in_amount: i.stocking_in_amount,
                  }
                : {
                      cost_price: source === 'transfer' ? null : computeCostPrice(i),
                      direct_storage: source === 'transfer' ? true : i.stocked_in_amount ? !!i.direct_storage : false,
                      goods_name: i.goods.name,
                      order_goods_id: i.id,
                      stock_in_amount: i.stocked_in_amount,
                  },
        )
        // console.info('formatList:', formatList)
        createStockIn.run({
            stock_in_order_id: props.id,
            is_one_key: sub,
            eta,
            goods_list: formatList,
            is_create_new,
        })
    }

    return (
        <BaseDrawer
            title="商品入库"
            isShow
            width={890}
            onClose={() => {
                layerManage.pop()
            }}
        >
            <Column data-height-100p>
                <Expaned data-overflow-auto>
                    <Spin spinning={loading}>
                        {source === 'purchase' && (
                            <React.Fragment>
                                <FormTitle hasLine={false} title="预计到货日期"></FormTitle>
                                <Row className={cls('date-container')}>
                                    <div
                                        className={cls('date-container', day === 0 ? 'item-active' : 'item')}
                                        onClick={() => handleDay(0)}
                                    >
                                        今天
                                        <div className="des" data-margin-left-8>
                                            {formatDate(dayjs())}
                                        </div>
                                    </div>
                                    <div
                                        className={cls('date-container', day === 1 ? 'item-active' : 'item')}
                                        onClick={() => handleDay(1)}
                                    >
                                        明天
                                        <div className="des" data-margin-left-8>
                                            {formatDate(dayjs().add(1, 'day'))}
                                        </div>
                                    </div>
                                    <div
                                        className={cls('date-container', day === 2 ? 'item-active' : 'item')}
                                        onClick={() => handleDay(2)}
                                    >
                                        后天
                                        <div className="des" data-margin-left-8>
                                            {formatDate(dayjs().add(2, 'day'))}
                                        </div>
                                    </div>
                                    <div
                                        className={classnames(
                                            cls('date-container', day === 3 ? 'item-active' : 'item'),
                                            'time-picker',
                                        )}
                                    >
                                        <DatePicker
                                            bordered={false}
                                            allowClear={false}
                                            placeholder="选择日期"
                                            value={day === 3 ? moment(eta.replace('-', '/'), 'YYYY/MM/DD') : void 0}
                                            disabledDate={current => current <= dayjs().add(2, 'day')}
                                            dateRender={current => (
                                                <div
                                                    className="ant-picker-cell-inner"
                                                    style={{
                                                        borderRadius: '50%',
                                                    }}
                                                >
                                                    {current.date()}
                                                </div>
                                            )}
                                            onChange={(monent, dateString) => {
                                                setDay(3)
                                                setEta(dateString)
                                            }}
                                            suffixIcon={<i className="iconfont iconshijianshaixuan" />}
                                        />
                                    </div>
                                </Row>
                            </React.Fragment>
                        )}
                        <FormTitle hasLine={false} title="商品清单" />
                        <div
                            style={{
                                width: 'calc(100% - 32px)',
                                marginLeft: '16px',
                            }}
                        >
                            <Table
                                rowKey={record => record.id}
                                bordered
                                pagination={false}
                                dataSource={stockInList}
                                columns={columns}
                            ></Table>
                        </div>
                    </Spin>
                </Expaned>
                <div className={cls('bottom')}>
                    {source === 'purchase' && (
                        <Row
                            data-align-items-center
                            className="text-btn-blue"
                            onClick={() => {
                                if (createStockIn.loading) return
                                handleSubmit(true, true)
                            }}
                        >
                            <i
                                data-margin-right-4
                                data-font-size-20
                                className="iconfont iconwenzi"
                                style={{
                                    fontWeight: 500,
                                }}
                            />
                            一键入库
                        </Row>
                    )}
                    <Button disabled={btnDisabled} types="default" onClick={handlePreview}>
                        确认入库
                    </Button>
                </div>
            </Column>
        </BaseDrawer>
    )
}

export default StockJoinForm
